import type { JSONContent } from "@tiptap/react"

// keep in sync with the validation of the news-api
const linkPattern = /(\b[a-z][a-z\d+.-]*:\/\/|\bwww\.|\bmailto:)\S/i

export const containsLink = (content: string): boolean => linkPattern.test(content)

interface SupportedSchema {
	/** the only level of heading that is supported */
	headingLevel: number
	marks: ReadonlySet<string>
	nodes: ReadonlySet<string>
}

// The editor discards the whole content if it contains a node it does not know, e.g. a quote from "> hi",
// or a heading of a level it does not support, e.g. from "# hi".
// Unsupported nodes are therefore turned into paragraphs, so that their text stays visible.
export const toSupportedContent = (content: JSONContent, schema: SupportedSchema): JSONContent => {
	const convert = (node: JSONContent): JSONContent[] => {
		if (node.type === "text") {
			const marks = node.marks?.filter((mark) => schema.marks.has(mark.type))
			return [{ ...node, marks: marks?.length ? marks : undefined }]
		}

		const children = node.content?.flatMap(convert)

		const isUnsupportedHeading = node.type === "heading" && Number(node.attrs?.level ?? 1) !== schema.headingLevel

		if (node.type && schema.nodes.has(node.type) && !isUnsupportedHeading) {
			return [{ ...node, content: children }]
		}

		if (!children?.length) {
			return []
		}

		if (children.some((child) => child.type !== "text")) {
			return children
		}

		const prefix = node.type === "heading" ? `${"#".repeat(Number(node.attrs?.level ?? 1))} ` : ""
		return [{ content: prefix ? [{ text: prefix, type: "text" }, ...children] : children, type: "paragraph" }]
	}

	return { ...content, content: content.content?.flatMap(convert) }
}
